import { ref, onMounted } from 'vue'
import { useDark } from '@vueuse/core'

export function useTelegramWebApp() {
  const telegramWebApp = ref(null)
  const isReady = ref(false)
  let isDark = null
  
  try {
    const darkMode = useDark()
    isDark = darkMode?.isDark || ref(false)
  } catch (e) {
    isDark = ref(false)
  }

  const initTelegramWebApp = async () => {
    // Проверяем, что мы в Telegram WebApp
    if (typeof window === 'undefined' || !window.Telegram?.WebApp) {
      console.log('[Telegram WebApp] Not available, running in browser mode')
      isReady.value = true
      return
    }

    try {
      telegramWebApp.value = window.Telegram.WebApp
      
      // Инициализация
      telegramWebApp.value.ready()
      telegramWebApp.value.expand()
      
      // Настройка темы
      setupTheme()
      
      // Настройка кнопок
      setupButtons()
      
      isReady.value = true
      console.log('[Telegram WebApp] Initialized successfully')
    } catch (error) {
      console.error('[Telegram WebApp] Initialization error:', error)
      isReady.value = true // Продолжаем работу даже при ошибке
    }
  }

  const setupTheme = () => {
    if (!telegramWebApp.value) return

    try {
      const colorScheme = telegramWebApp.value.colorScheme
      
      // Применяем тему
      if (colorScheme === 'dark') {
        document.body.classList.add('telegram-dark')
        if (isDark && isDark.value !== undefined) {
          isDark.value = true
        }
      } else {
        document.body.classList.remove('telegram-dark')
        if (isDark && isDark.value !== undefined) {
          isDark.value = false
        }
      }

      // Устанавливаем цвет заголовка (поддерживается с Telegram WebApp API 6.1+)
      const hasVersionCheck = typeof telegramWebApp.value.isVersionAtLeast === 'function'
      const isV61Plus = hasVersionCheck ? telegramWebApp.value.isVersionAtLeast('6.1') : true

      if (isV61Plus && typeof telegramWebApp.value.setHeaderColor === 'function') {
        try {
          telegramWebApp.value.setHeaderColor('#ff6530')
        } catch (e) {
          // ignore unsupported version error
        }
      }
      
      // Устанавливаем цвет фона
      if (isV61Plus && typeof telegramWebApp.value.setBackgroundColor === 'function') {
        try {
          telegramWebApp.value.setBackgroundColor('#f5f5f5')
        } catch (e) {
          // ignore unsupported version error
        }
      }
    } catch (error) {
      console.warn('[Telegram WebApp] Theme setup error:', error)
    }
  }

  const setupButtons = () => {
    if (!telegramWebApp.value) return

    // BackButton вызывает проблемы с proxy в некоторых версиях Telegram WebApp
    // Пропускаем настройку BackButton - навигация работает через роутер
    // Если нужно, можно добавить позже через другой подход
  }

  const showBackButton = () => {
    if (telegramWebApp.value) {
      telegramWebApp.value.BackButton.show()
    }
  }

  const hideBackButton = () => {
    if (telegramWebApp.value) {
      telegramWebApp.value.BackButton.hide()
    }
  }

  const showMainButton = (text, onClick) => {
    if (!telegramWebApp.value) return

    telegramWebApp.value.MainButton.setText(text)
    telegramWebApp.value.MainButton.onClick(onClick)
    telegramWebApp.value.MainButton.show()
  }

  const hideMainButton = () => {
    if (telegramWebApp.value) {
      telegramWebApp.value.MainButton.hide()
    }
  }

  const setMainButtonText = (text) => {
    if (telegramWebApp.value) {
      telegramWebApp.value.MainButton.setText(text)
    }
  }

  const showAlert = (message) => {
    if (telegramWebApp.value) {
      telegramWebApp.value.showAlert(message)
    } else {
      alert(message)
    }
  }

  const showConfirm = (message) => {
    return new Promise((resolve) => {
      if (telegramWebApp.value) {
        telegramWebApp.value.showConfirm(message, (confirmed) => {
          resolve(confirmed)
        })
      } else {
        resolve(confirm(message))
      }
    })
  }

  const hapticFeedback = (type = 'impact', style = 'medium') => {
    if (telegramWebApp.value?.HapticFeedback) {
      telegramWebApp.value.HapticFeedback.impactOccurred(style)
    }
  }

  onMounted(() => {
    initTelegramWebApp()
  })

  return {
    telegramWebApp,
    isReady,
    initTelegramWebApp,
    showBackButton,
    hideBackButton,
    showMainButton,
    hideMainButton,
    setMainButtonText,
    showAlert,
    showConfirm,
    hapticFeedback
  }
}

